/**
 * The clinic site as structured data (site builder v2; the API's website-content.ts is the
 * contract). One renderer draws it for the editor preview and the public page.
 */
export const SITE_TEMPLATES = ['acolhedor', 'editorial', 'luminoso'] as const;
export const SITE_FONTS = ['newsreader-figtree', 'literata-inter', 'figtree'] as const;
export const SECTION_KINDS = ['hero', 'about', 'services', 'team', 'steps', 'faq', 'contact', 'booking'] as const;
export const SITE_ICONS = ['brain', 'puzzle', 'users', 'heart', 'child', 'leaf', 'chat', 'star'] as const;

export type SiteTemplate = (typeof SITE_TEMPLATES)[number];
export type SiteFontPair = (typeof SITE_FONTS)[number];
export type SectionKind = (typeof SECTION_KINDS)[number];
export type SiteIcon = (typeof SITE_ICONS)[number];

export type SiteTheme = { template: SiteTemplate; fontPair: SiteFontPair; primary: string; background: string; accent: string };
export type SiteItem = { title: string; body: string; icon?: SiteIcon };
export type SiteContact = { whatsapp: string; instagram: string; email: string; showAddress: boolean };
export const SITE_TEAM_LAYOUTS = ['compact', 'side', 'top'] as const;
export type SiteTeamLayout = (typeof SITE_TEAM_LAYOUTS)[number];
export const SITE_TEAM_ARRANGES = ['row', 'stack'] as const;
export type SiteTeamArrange = (typeof SITE_TEAM_ARRANGES)[number];

export type SiteSection = {
  kind: SectionKind;
  visible: boolean;
  title: string;
  body: string;
  eyebrow?: string;
  photoId?: string | null;
  items?: SiteItem[];
  contact?: SiteContact;
  /** Team only: a small row, a card with the photo beside the text, or on top of it. */
  layout?: SiteTeamLayout;
  /** Team only: people side by side, or one above the other. */
  arrange?: SiteTeamArrange;
};
export type ClinicSiteContent = { v: 2; theme: SiteTheme; sections: SiteSection[] };

export type SiteAddress = { line: string; place: string } | null;

/** A professional shown in "Quem atende", from the published booking profiles. */
export type SiteTeamMember = { id: string; name: string; role: string; registration?: string; about?: string; photoUrl?: string };

export type ClinicSiteState = {
  clinic: { id: string; name: string; slug: string; address: SiteAddress; logoUpdatedAt: string | null };
  team: SiteTeamMember[];
  version: number;
  draft: ClinicSiteContent;
  saved: boolean;
  published: boolean;
  /** The public page shows exactly this draft. */
  upToDate: boolean;
};

/** What the public page receives from the API. */
export type PublishedSite = {
  clinicId: string;
  name: string;
  address: SiteAddress;
  logoUpdatedAt: string | null;
  domain: string | null;
  content: ClinicSiteContent;
};

export type SiteRef = {
  id: string;
  kind: 'image' | 'link' | 'instagram';
  url: string | null;
  handle: string | null;
  note: string;
  look: { title: string; colors: string[]; fonts: string[] } | null;
  unreadable: boolean;
};

export type SiteProposal = { name: string; content: ClinicSiteContent };

export type DomainRecord = { type: 'CNAME' | 'TXT'; name: string; fqdn: string; value: string; ok: boolean };
export type SiteDomain = {
  hostname: string;
  status: 'pending' | 'active' | 'failed';
  records: DomainRecord[];
  failed: boolean;
  problem: 'removed' | 'blocked' | 'moved' | 'certificate' | null;
  checkedAt: string | null;
  activatedAt: string | null;
};
export type SiteDomainState = { available: boolean; domain: SiteDomain | null };


const API = () => (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4333').replace(/\/$/, '');

/** A site photo, served by the API's public route. */
export const sitePhotoUrl = (photoId: string) => `${API()}/api/public/site-photos/${encodeURIComponent(photoId)}`;

/**
 * Where the clinic's site lives without its own domain (owner, 2026-11-03: lanuq.com/c/<slug>).
 * NEXT_PUBLIC_SITE_URL is the public origin; without it the app's own origin serves /c/<slug>.
 */
export function clinicSitePath(slug: string) {
  return `/c/${encodeURIComponent(slug)}`;
}
export function clinicSiteAddress(slug: string) {
  const base = (process.env.NEXT_PUBLIC_SITE_URL ?? '').replace(/\/$/, '');
  return `${base}${clinicSitePath(slug)}`;
}
/** The address without the scheme, as people read it. */
export const displayAddress = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

/** Font pairs: display face for headings, text face for the rest. */
export const SITE_FONT_FAMILIES: Record<SiteFontPair, { display: string; text: string; google: string }> = {
  'newsreader-figtree': {
    display: "'Newsreader', Georgia, serif",
    text: "'Figtree', Arial, sans-serif",
    google: 'family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,600',
  },
  'literata-inter': {
    display: "'Literata', Georgia, serif",
    text: "'Inter', Arial, sans-serif",
    google: 'family=Literata:opsz,wght@7..72,400;7..72,500;7..72,600&family=Inter:wght@400;500;600',
  },
  figtree: { display: "'Figtree', Arial, sans-serif", text: "'Figtree', Arial, sans-serif", google: '' },
};

/** Presets offered next to "your own colour". */
export const SITE_PRIMARY_PRESETS = ['#3e5c4a', '#2f4f43', '#1f2e28', '#2f4a6b', '#6b2f4a', '#5b4e8a'];
export const SITE_BACKGROUND_PRESETS = ['#f7f3ec', '#ffffff', '#f7f5f0', '#eef3f8', '#f8ece8', '#efe7da'];
export const SITE_ACCENT_PRESETS = ['#b4573e', '#c9826b', '#dd796b', '#d4a24c', '#e6a7af', '#5b8db8'];

function luminance(hex: string) {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
const contrast = (a: string, b: string) => {
  const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
};
/** Readable ink over a colour: dark on light colours, white on dark ones. */
export const inkOn = (hex: string) => (contrast(hex, '#ffffff') >= 4.5 ? '#ffffff' : '#1f2422');

/** Every colour the site paints with, from the clinic's three choices. */
export function siteColors(theme: SiteTheme) {
  const ink = inkOn(theme.background);
  const dark = ink === '#1f2422';
  return {
    '--site-bg': theme.background,
    '--site-ink': ink,
    '--site-muted': dark ? 'rgba(31,36,34,0.72)' : 'rgba(255,255,255,0.78)',
    '--site-line': dark ? 'rgba(31,36,34,0.12)' : 'rgba(255,255,255,0.2)',
    '--site-card': dark ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.08)',
    '--site-primary': theme.primary,
    '--site-on-primary': inkOn(theme.primary),
    '--site-accent': theme.accent,
    // The accent as text only when it reads on the background; the ink otherwise.
    '--site-accent-ink': contrast(theme.accent, theme.background) >= 3 ? theme.accent : ink,
    '--site-tint': `color-mix(in srgb, ${theme.primary} 10%, ${theme.background})`,
  } as Record<string, string>;
}

/** WhatsApp link from the digits the clinic typed (Brazil when no country code). */
export const whatsappHref = (digits: string) => `https://wa.me/${digits.length <= 11 ? `55${digits}` : digits}`;

/** The API's limit for a site photo or reference image, checked before the upload. */
export const MAX_SITE_IMAGE_BYTES = 2 * 1024 * 1024;
export const MAX_SITE_PHOTOS = 12;

/** Error codes the site builder translates (websites.errors.*); anything else is "failed". */
export const SITE_ERRORS = [
  'conflict',
  'failed',
  'site_content_invalid',
  'site_proposals_limit',
  'site_proposal_needs_input',
  'site_revision_unapplied',
  'site_refs_full',
  'site_ref_link_invalid',
  'site_ref_handle_invalid',
  'site_image_invalid',
  'site_photos_full',
  'site_photo_in_use',
  'site_domain_invalid',
  'site_domain_taken',
  'site_domain_exists',
  'site_domains_unavailable',
  'site_domain_missing',
  'ai_unavailable',
] as const;
export type SiteError = (typeof SITE_ERRORS)[number];
export type SiteResult<T> = { data: T; error?: undefined } | { data?: undefined; error: SiteError };
